import CookieUtils from 'utils/cookies';
import { AUTH_API_DOMAIN } from 'constants/api';
import { generateRequestHeaders } from './api';
import { fetchHandler } from './utils/fetchHandler';
import { AUTHENTICATION_ACTIONS, telemetry } from 'utils/telemetry';
import { ALLOWED_SCOPES, ADMIN_PORTAL_ROLES } from 'constants/roles';
import { GenericError } from '@auth0/auth0-react';
import crossTabTokenRefreshService from './crossTabTokenRefreshServiceSingleton';
import { getAuth0TokenGetter } from './auth0TokenGetter';
import type { Auth0TokenGetter } from './auth0TokenGetter';
import { HTTPError } from 'utils/HTTPError';
import { getAccessTokenPermissions } from 'utils/auth0';
import { replaceLocation } from 'utils/navigation';
import routes from 'routes';

const MFA_DEVICE_ID_BACKUP = 1;
const MFA_DEVICE_ID_SMS = 2;
const MFA_DEVICE_ID_APP = 3;

// Auth messages via query params
const NO_EXISTING_SSO_CONNECTION = 'NO_EXISTING_SSO_CONNECTION';

const friendMFADeviceName = (mfa_type_id) => {
  switch (mfa_type_id) {
    case MFA_DEVICE_ID_BACKUP:
      return 'Backup Codes';
    case MFA_DEVICE_ID_SMS:
      return 'SMS';
    case MFA_DEVICE_ID_APP:
      return 'Authenticator App';
    default:
      return 'Unknown';
  }
};

export type OauthAccessTokenData = {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  scopes: string[];
  token_type: string;
  permission: string[];
};

// Regenerate an SSO client SP encryption data
const generateSSOClientSPEncryption = async (sso_client_id) => {
  const response = await fetchHandler(`${AUTH_API_DOMAIN}/v1/sso-clients/${sso_client_id}/generate-sp-encryption`, {
    headers: generateRequestHeaders(),
    method: 'PUT',
  });

  return response.status === 204;
};

const _clearSession = () => {
  CookieUtils.clearHeaders();
};

const _setExpirationTime = (accessExpiresIn: number) => {
  const accessNow = new Date();

  accessNow.setSeconds(accessNow.getSeconds() + accessExpiresIn);
  const accessNowTime = accessNow.getTime();

  CookieUtils.updateTokenExpiration(accessNowTime);
};

// Auth0 error codes meaning the Auth0 session itself is gone, so no new token can be issued
// without signing in again. Anything else (e.g. a timeout) may be transient.
const AUTH0_SESSION_ENDED_ERRORS = new Set([
  'consent_required',
  'interaction_required',
  'invalid_grant',
  'login_required',
  'missing_refresh_token',
]);

const refreshAuth0AccessToken = async (getAuth0Token: Auth0TokenGetter): Promise<void> => {
  telemetry.addAction(AUTHENTICATION_ACTIONS.TOKEN_REFRESH_REQUEST, { provider: 'auth0' });

  try {
    // The API just rejected the token we hold, so skip the SDK cache and ask Auth0 for a new one.
    const accessToken = await getAuth0Token({ cacheMode: 'off' });

    if (!accessToken) {
      throw new Error('Could not fetch Auth0 access token.');
    }

    // Save the roles alongside the token, as the Diglet refresh does, so UI gated on the `scopes`
    // cookie follows the token the API enforces. Only the `admin_` token: the unprefixed
    // `access_token` cookie is a Compass handoff, which Auth0 sessions don't use.
    CookieUtils.updateHeaders({ admin_access_token: accessToken, scopes: getAccessTokenPermissions(accessToken) });
    telemetry.addAction(AUTHENTICATION_ACTIONS.TOKEN_REFRESH_SUCCESS, { provider: 'auth0' });
  } catch (e) {
    if (e instanceof GenericError && AUTH0_SESSION_ENDED_ERRORS.has(e.error)) {
      telemetry.addAction(AUTHENTICATION_ACTIONS.TOKEN_REFRESH_EXPIRED_REDIRECT_TO_LOGOUT, { provider: 'auth0' });
      replaceLocation(routes.Logout.as);
    } else {
      telemetry.addError(e, {
        description: AUTHENTICATION_ACTIONS.TOKEN_REFRESH_FAILURE,
        provider: 'auth0',
      });
    }

    // Reject either way, as the Diglet refresh below does.
    throw e;
  }
};

const refreshAccessToken = async (): Promise<OauthAccessTokenData | void> => {
  const getAuth0Token = getAuth0TokenGetter();

  if (getAuth0Token) {
    return refreshAuth0AccessToken(getAuth0Token);
  }

  return crossTabTokenRefreshService.refreshAccessToken().catch((e) => {
    // If the refresh token itself has expired, redirect to the logout route
    if (e instanceof HTTPError && e.response.status === 401) {
      telemetry.addAction(AUTHENTICATION_ACTIONS.TOKEN_REFRESH_EXPIRED_REDIRECT_TO_LOGOUT);
      replaceLocation(routes.Logout.as);
    } else {
      telemetry.addError(e, {
        description: AUTHENTICATION_ACTIONS.TOKEN_REFRESH_FAILURE,
      });
    }

    // Reject either way, so that callers in fetchHandler and Apollo Link don't treat returns as
    // successfully resolved responses.
    throw e;
  });
};

const shouldLogoutBasedOnScopes = (scopes) => {
  if (!scopes || scopes.length === 0 || typeof scopes === 'string') return true;

  const isAdminPortalUser = ADMIN_PORTAL_ROLES.some((role) => scopes.includes(role));
  const isCompassUser = scopes.some((scope) => scope.includes('compass'));

  if (!isAdminPortalUser && isCompassUser) return true;

  return isAdminPortalUser ? false : true;
};

const checkScopes = (username: string | undefined, allowedScopes: string[], response) => {
  if (response.error) throw response;

  const { expires_in, scopes } = response;

  const union = (a, b) => Array.from(new Set([...a, ...b]));

  // User doesn't have ANY scopes OR
  // The intersection of the user scopes and the allowed scopes
  // isn't less than the length of both combined.
  //
  // If they have ONE of the scopes, the length of the union
  // would be less than the 2 arrays combined.
  //
  // [1,2] union [2,3] === [1,2,3]
  // It's not the case that [1,2,3].length >= [1,2].length + [2,3].length
  if (allowedScopes) {
    if (!scopes || union(scopes, allowedScopes).length >= scopes.length + allowedScopes.length) {
      throw { error_description: 'Invalid credentials' };
    }
  }

  _setExpirationTime(expires_in);
  CookieUtils.saveHeaders(response, username);
};

const requestAccessToken = (credentials: Record<string, unknown>, username: string | undefined, allowedScopes: any) => {
  return fetch(`${AUTH_API_DOMAIN}/oauth/access_token`, {
    headers: generateRequestHeaders(),
    method: 'POST',
    body: JSON.stringify({
      // issueToken in Diglet Oauth controller
      ...credentials,
      client_id: process.env.CLIENT_ID,
      client_secret: process.env.CLIENT_SECRET,
      scope: ALLOWED_SCOPES.join(' '),
    }),
  })
    .then(async (res) => {
      const response = await res.json();
      checkScopes(username, allowedScopes, response);
      return getMe(response.scopes);
    })
    .catch((err) => {
      throw err;
    });
};

const signIn = (username: string, password: string, allowedScopes: any, params = {}) => {
  return requestAccessToken({ username, password, grant_type: 'password', ...params }, username, allowedScopes);
};

const signInWithOneTimePassword = (relayToken: string, relayOtp: string, allowedScopes: any) => {
  return requestAccessToken(
    { grant_type: 'one_time_password', relay_token: relayToken, relay_otp: relayOtp },
    undefined,
    allowedScopes
  );
};

const logout = () => {
  const access_token = CookieUtils.get('admin_access_token');

  if (!access_token) {
    _clearSession();
    return;
  }
  //revokeSession in diglet Oauth controller
  return fetch(`${AUTH_API_DOMAIN}/oauth/session`, {
    headers: generateRequestHeaders(),
    method: 'DELETE',
  })
    .then(() => {
      _clearSession();
      return;
    })
    .catch(() => {
      _clearSession();
    });
};

const forgotPassword = (email, redirect) => {
  return fetch(`${AUTH_API_DOMAIN}/v1/users/passwords/forgot?username=${email}&app_url=${redirect}`)
    .then((response) => {
      if (response.status === 204) {
        return 'Reset Password Email Sent';
      }
    })
    .catch((err: Error) => {
      throw err;
    });
};

const resetPassword = (username, password, token) => {
  return fetch(`${AUTH_API_DOMAIN}/v1/users/passwords/reset`, {
    headers: generateRequestHeaders(),
    method: 'POST',
    body: JSON.stringify({
      username,
      password,
      token,
    }),
  });
};

const createMFAAccessToken = (mfaToken, send_to_email = false) => {
  return fetch(`${AUTH_API_DOMAIN}/v1/mfa/challenge`, {
    headers: {
      ...generateRequestHeaders(),
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({
      mfa_token: mfaToken,
      ...(send_to_email && { send_to_email }),
    }),
  })
    .then(async (response) => {
      if (response.status === 200) {
        const res = await response.json();
        return res.data.challenge;
      }
    })
    .catch((err: Error) => {
      throw err;
    });
};

const mfaRegisterDevice = (mfa_type_id, payload = {}) => {
  return fetch(`${AUTH_API_DOMAIN}/v1/me/mfa-devices`, {
    headers: generateRequestHeaders(),
    body: JSON.stringify({ mfa_type_id, payload }),
    method: 'POST',
  })
    .then(async (response) => {
      return response.json();
    })
    .catch((err: Error) => {
      throw err;
    });
};

const mfaVerifyDevice = (mfa_device_id, payload) => {
  return fetch(`${AUTH_API_DOMAIN}/v1/me/mfa-devices/${mfa_device_id}/verify`, {
    headers: generateRequestHeaders(),
    body: JSON.stringify({
      payload,
    }),
    method: 'POST',
  })
    .then(async (response) => {
      // A rejected MFA code (e.g. an expired or incorrect code) comes back as a
      // non-2xx response with an error body. Surface it as an error so callers can
      // distinguish failure from success, rather than resolving with the error body.
      if (!response.ok) {
        throw new HTTPError(response);
      }

      return response.json();
    })
    .catch((err) => {
      throw err;
    });
};

const mfaSignIn = (challenge, mfaToken, username, password, allowedScopes, params) => {
  return fetch(`${AUTH_API_DOMAIN}/oauth/access_token`, {
    headers: generateRequestHeaders(),
    method: 'POST',
    body: JSON.stringify({
      //issueToken in diglet Oauth controller
      username,
      password,
      mfa_token: mfaToken,
      challenge_response: {
        code: challenge,
      },
      grant_type: 'mfa_challenge_response',
      scope: ALLOWED_SCOPES.join(' '),
      client_id: process.env.CLIENT_ID,
      client_secret: process.env.CLIENT_SECRET,
      ...params,
    }),
  })
    .then(async (res) => {
      const response = await res.json();
      if (response.error) throw response;
      checkScopes(username, allowedScopes, response);
      return getMe(response.scopes); //show function in diglet MeConroller, returns user data
    })
    .catch((err: Error) => {
      throw err;
    });
};

const getMe = async (scopes = []) => {
  const response = await fetchHandler(`${AUTH_API_DOMAIN}/v1/me`, {
    headers: generateRequestHeaders(),
  });

  const data = await response.json();

  return { ...data, scopes };
};

// Get user roles
const getUserRoles = async () => {
  const response = await fetchHandler(AUTH_API_DOMAIN + '/v1/roles', {
    headers: generateRequestHeaders(),
  });

  return response.json();
};

export default {
  MFA_DEVICE_ID_BACKUP,
  MFA_DEVICE_ID_APP,
  MFA_DEVICE_ID_SMS,
  NO_EXISTING_SSO_CONNECTION,
  friendMFADeviceName,
  signIn,
  signInWithOneTimePassword,
  logout,
  forgotPassword,
  resetPassword,
  checkScopes,
  createMFAAccessToken,
  mfaRegisterDevice,
  mfaVerifyDevice,
  mfaSignIn,
  getMe,
  getUserRoles,
  generateSSOClientSPEncryption,
  shouldLogoutBasedOnScopes,
  refreshAccessToken,
};
